import {
  BookOpen,
  Bot,
  Brain,
  ChevronRight,
  Clock,
  Database,
  ExternalLink,
  Link,
  Loader2,
  MessageSquare,
  Sparkles,
  StickyNote,
  Trash2,
  Upload,
} from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AddNoteModal } from "@/components/data-sources";
import {
  formatFileSize,
  getActivityIcon,
  getFileIcon,
  getFileStatusConfig,
  timeAgo,
} from "@/components/project-hub/utils";
import { useConfirm, useToast } from "@/components/shared";
import { UPLOAD_ACCEPT_STRING } from "@/constants/files";
import { buildChatSessionPath, buildNewChatPath } from "@/lib/chatNavigation";
import { dataSourceService, projectService } from "@/lib/services";
import type { ProjectActivity, ProjectHub as ProjectHubType, ProjectKnowledgeBaseFile } from "@/types";

// ============================================================================
// HUB TAB - The main landing experience (2-column layout)
// ============================================================================

export function HubTab({
  hub,
  navigate,
  canEdit,
  projectId,
  onRefresh,
}: Readonly<{
  hub: ProjectHubType;
  navigate: ReturnType<typeof useNavigate>;
  canEdit: boolean;
  projectId: string;
  onRefresh: () => Promise<void> | void;
}>) {
  const agents = hub.resources.filter((r) => r.resourceType === "agent");

  return (
    <div className="p-6 space-y-8 max-w-[1400px] mx-auto">
      {/* Welcome Banner */}
      <div className="bg-linear-to-r from-brand-primary/10 via-brand-secondary/10 to-brand-primary/5 border border-brand-primary/20 rounded-2xl p-6">
        <div className="flex items-start justify-between">
          <div>
            <h2 className="text-2xl font-bold text-content-primary">Welcome to {hub.name}</h2>
            <p className="text-content-tertiary mt-1">
              {hub.description ||
                `Project hub — ${hub.stats.memberCount} ${hub.stats.memberCount === 1 ? "member" : "members"}, ${hub.stats.agentCount} ${hub.stats.agentCount === 1 ? "agent" : "agents"}, and ${hub.defaultDataSource?.fileCount ?? 0} knowledge base files.`}
            </p>
          </div>
        </div>
      </div>

      {/* Main 2-column layout */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Left column - Quick Actions, Expert Agent, Agents, Recent Chats */}
        <div className="lg:col-span-2 space-y-8">
          {/* Quick Actions */}
          <section>
            <div className="flex items-center gap-2 mb-4">
              <Sparkles className="h-5 w-5 text-brand-primary" />
              <h2 className="text-lg font-semibold text-content-primary">Quick Actions</h2>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {/* Start a plain conversation (no agent) */}
              <button
                onClick={() => navigate(buildNewChatPath(hub.uuid))}
                className="w-full bg-surface-primary border border-border-primary rounded-xl p-4 hover:border-brand-primary/40 transition-all text-left group"
              >
                <div className="flex items-start gap-3">
                  <div className="h-10 w-10 rounded-lg bg-linear-to-br from-emerald-500/20 to-teal-500/20 flex items-center justify-center shrink-0">
                    <MessageSquare className="h-5 w-5 text-emerald-400" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="font-medium text-content-primary text-sm group-hover:text-brand-primary transition-colors">
                      New conversation
                    </h3>
                    <p className="text-xs text-content-tertiary mt-0.5">Start a chat in this project</p>
                  </div>
                </div>
              </button>

              {/* Start a conversation with the expert agent (only shown when agent exists) */}
              {hub.defaultAgent && (
                <button
                  onClick={() => navigate(buildNewChatPath(hub.uuid), { state: { agent: hub.defaultAgent?.uuid } })}
                  className="w-full bg-surface-primary border border-border-primary rounded-xl p-4 hover:border-brand-primary/40 transition-all text-left group"
                >
                  <div className="flex items-start gap-3">
                    <div
                      className="h-10 w-10 rounded-lg flex items-center justify-center shrink-0"
                      style={{ backgroundColor: `${hub.defaultAgent.color}20` }}
                    >
                      <Bot className="h-5 w-5" style={{ color: hub.defaultAgent.color }} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="font-medium text-content-primary text-sm group-hover:text-brand-primary transition-colors">
                        Ask {hub.defaultAgent.name}
                      </h3>
                      <p className="text-xs text-content-tertiary mt-0.5">Chat with the project expert agent</p>
                    </div>
                  </div>
                </button>
              )}
            </div>
          </section>

          {/* Project Expert Agent (hero card) */}
          {hub.defaultAgent && (
            <section>
              <div className="flex items-center gap-2 mb-4">
                <Brain className="h-5 w-5 text-brand-secondary" />
                <h2 className="text-lg font-semibold text-content-primary">Project Expert</h2>
              </div>
              <button
                onClick={() => navigate(buildNewChatPath(hub.uuid), { state: { agent: hub.defaultAgent?.uuid } })}
                className="w-full bg-linear-to-br from-brand-secondary/5 to-brand-primary/5 border border-brand-secondary/20 rounded-xl p-5 hover:border-brand-secondary/40 transition-all text-left group"
              >
                <div className="flex items-start gap-4">
                  <div
                    className="h-12 w-12 rounded-xl flex items-center justify-center shrink-0"
                    style={{ backgroundColor: `${hub.defaultAgent.color}20` }}
                  >
                    <Bot className="h-6 w-6" style={{ color: hub.defaultAgent.color }} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="font-semibold text-content-primary group-hover:text-brand-primary transition-colors">
                      {hub.defaultAgent.name}
                    </h3>
                    <p className="text-sm text-content-tertiary mt-1">
                      {hub.defaultAgent.description ||
                        "Your AI expert for this project. Has access to the knowledge base and can answer questions about project context, decisions, and documents."}
                    </p>
                    <div className="flex items-center gap-2 mt-3">
                      <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-brand-primary/10 text-xs font-medium text-brand-primary">
                        <BookOpen className="h-3 w-3" />
                        {hub.defaultDataSource?.fileCount ?? 0} KB files
                      </span>
                      <span className="text-xs text-content-quaternary">Auto-created project assistant</span>
                    </div>
                  </div>
                  <ChevronRight className="h-5 w-5 text-content-quaternary group-hover:text-brand-primary transition-colors mt-1" />
                </div>
              </button>
            </section>
          )}

          {/* Other Project Agents (excluding the expert) */}
          {agents.some((a) => a.agentUuid !== hub.defaultAgent?.uuid) && (
            <section>
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <Bot className="h-5 w-5 text-brand-secondary" />
                  <h2 className="text-lg font-semibold text-content-primary">Other Agents</h2>
                </div>
                <button
                  onClick={() => navigate("/agents")}
                  className="text-sm text-brand-primary hover:text-brand-primary/80 font-medium"
                >
                  View all
                </button>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {agents
                  .filter((a) => a.agentUuid !== hub.defaultAgent?.uuid)
                  .map((resource) => (
                    <button
                      key={resource.uuid}
                      onClick={() =>
                        resource.agentUuid &&
                        navigate(buildNewChatPath(hub.uuid), { state: { agent: resource.agentUuid } })
                      }
                      className="bg-surface-primary border border-border-primary rounded-xl p-4 hover:border-border-secondary transition-colors text-left group"
                    >
                      <div className="flex items-start gap-3">
                        <div
                          className="h-10 w-10 rounded-lg flex items-center justify-center shrink-0"
                          style={{
                            backgroundColor: `${resource.agentColor || "#7C16F5"}20`,
                          }}
                        >
                          <Bot className="h-5 w-5" style={{ color: resource.agentColor || "#7C16F5" }} />
                        </div>
                        <div className="min-w-0 flex-1">
                          <h3 className="font-medium text-content-primary text-sm truncate group-hover:text-brand-primary transition-colors">
                            {resource.agentName || "Unnamed Agent"}
                          </h3>
                          <p className="text-xs text-content-quaternary mt-1">
                            {resource.addedByName && `Added by ${resource.addedByName}`}
                          </p>
                        </div>
                        <ChevronRight className="h-4 w-4 text-content-quaternary group-hover:text-brand-primary transition-colors opacity-0 group-hover:opacity-100 mt-1" />
                      </div>
                    </button>
                  ))}
              </div>
            </section>
          )}

          {/* Recent Conversations (from hub.recentChats, user-private) */}
          {hub.recentChats.length > 0 && (
            <section>
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <MessageSquare className="h-5 w-5 text-brand-primary" />
                  <h2 className="text-lg font-semibold text-content-primary">Your Recent Conversations</h2>
                </div>
              </div>
              <div className="bg-surface-primary border border-border-primary rounded-xl overflow-hidden">
                {hub.recentChats.slice(0, 5).map((chat, idx) => (
                  <button
                    key={chat.uuid}
                    onClick={() => navigate(buildChatSessionPath(chat.uuid, hub.uuid))}
                    className={`w-full flex items-center gap-4 px-5 py-3.5 hover:bg-interactive-hover transition-colors text-left ${
                      idx < Math.min(hub.recentChats.length, 5) - 1 ? "border-b border-border-primary" : ""
                    }`}
                  >
                    <div className="h-9 w-9 rounded-lg bg-brand-primary/10 flex items-center justify-center shrink-0">
                      <MessageSquare className="h-4 w-4 text-brand-primary" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-content-primary truncate">
                        {chat.title || "Untitled conversation"}
                      </p>
                      <p className="text-[11px] text-content-quaternary mt-0.5">
                        {chat.agentName && `${chat.agentName} · `}
                        {chat.messageCount} messages
                      </p>
                    </div>
                    <span className="text-xs text-content-quaternary whitespace-nowrap shrink-0">
                      {timeAgo(chat.updatedAt)}
                    </span>
                  </button>
                ))}
              </div>
            </section>
          )}
        </div>

        {/* Right column - Knowledge Base, Activity Feed, Additional Data Sources */}
        <div className="space-y-8">
          {/* Knowledge Base */}
          <KnowledgeBasePanel hub={hub} canEdit={canEdit} projectId={projectId} onRefresh={onRefresh} />

          {/* Activity Feed */}
          {hub.activities.length > 0 && <ActivityFeed activities={hub.activities} />}

          {/* Additional Data Sources */}
          {hub.additionalDataSources.length > 0 && (
            <section>
              <div className="flex items-center gap-2 mb-4">
                <Database className="h-5 w-5 text-emerald-400" />
                <h2 className="text-lg font-semibold text-content-primary">Linked Data Sources</h2>
              </div>
              <div className="space-y-3">
                {hub.additionalDataSources.map((ds) => (
                  <button
                    key={ds.uuid}
                    onClick={() => navigate(`/data-sources/${ds.uuid}`)}
                    className="w-full bg-surface-primary border border-border-primary rounded-xl p-4 hover:border-border-secondary transition-colors text-left group"
                  >
                    <div className="flex items-center gap-3">
                      <div className="h-10 w-10 rounded-lg bg-emerald-500/10 flex items-center justify-center shrink-0">
                        <Database className="h-5 w-5 text-emerald-400" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <h3 className="font-medium text-content-primary text-sm group-hover:text-brand-primary transition-colors truncate">
                          {ds.name}
                        </h3>
                        <p className="text-xs text-content-tertiary mt-0.5">
                          {ds.fileCount} files &middot;{" "}
                          <span className="capitalize">{ds.sourceType.replace("_", " ")}</span>
                        </p>
                      </div>
                      <ChevronRight className="h-4 w-4 text-content-quaternary group-hover:text-brand-primary transition-colors" />
                    </div>
                  </button>
                ))}
              </div>
            </section>
          )}

          {/* Project Links */}
          {hub.resources.some((r) => r.resourceType === "link") && (
            <section>
              <div className="flex items-center gap-2 mb-4">
                <Link className="h-5 w-5 text-purple-400" />
                <h2 className="text-lg font-semibold text-content-primary">Links</h2>
              </div>
              <div className="space-y-2">
                {hub.resources
                  .filter((r) => r.resourceType === "link")
                  .map((resource) => (
                    <a
                      key={resource.uuid}
                      href={resource.url || "#"}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-3 px-4 py-3 bg-surface-primary border border-border-primary rounded-xl hover:border-border-secondary transition-colors group"
                    >
                      <div className="h-8 w-8 rounded-lg bg-purple-500/10 flex items-center justify-center shrink-0">
                        <Link className="h-4 w-4 text-purple-400" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-content-primary truncate group-hover:text-brand-primary transition-colors">
                          {resource.title || resource.url}
                        </p>
                      </div>
                      <ExternalLink className="h-3.5 w-3.5 text-content-quaternary shrink-0" />
                    </a>
                  ))}
              </div>
            </section>
          )}
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// ACTIVITY FEED (right sidebar in Hub tab)
// ============================================================================

function ActivityFeed({
  activities,
}: Readonly<{
  activities: ProjectActivity[];
}>) {
  return (
    <section>
      <div className="flex items-center gap-2 mb-4">
        <Clock className="h-5 w-5 text-content-quaternary" />
        <h2 className="text-lg font-semibold text-content-primary">Activity</h2>
      </div>
      <div className="bg-surface-primary border border-border-primary rounded-xl overflow-hidden">
        <div className="divide-y divide-border-primary">
          {activities.slice(0, 15).map((activity) => {
            const { icon: Icon, color, bg } = getActivityIcon(activity.activityType);
            return (
              <div key={activity.uuid} className="flex items-start gap-3 px-4 py-3">
                <div className={`h-7 w-7 rounded-lg ${bg} flex items-center justify-center shrink-0 mt-0.5`}>
                  <Icon className={`h-3.5 w-3.5 ${color}`} />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-content-primary leading-snug">{activity.title}</p>
                  {activity.description && (
                    <p className="text-xs text-content-tertiary mt-0.5 truncate">{activity.description}</p>
                  )}
                  <p className="text-[11px] text-content-quaternary mt-1">
                    {activity.userName && `${activity.userName} · `}
                    {timeAgo(activity.createdAt)}
                  </p>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

// ============================================================================
// KNOWLEDGE BASE PANEL (right sidebar in Hub tab)
// ============================================================================

function KnowledgeBasePanel({
  // NOSONAR -- file management panel with upload, drag-drop, and delete
  hub,
  canEdit,
  projectId,
  onRefresh,
}: Readonly<{
  hub: ProjectHubType;
  canEdit: boolean;
  projectId: string;
  onRefresh: () => Promise<void> | void;
}>) {
  const { showToast } = useToast();
  const { confirm } = useConfirm();
  const navigate = useNavigate();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [showNoteModal, setShowNoteModal] = useState(false);
  const [isAddingNote, setIsAddingNote] = useState(false);

  const kb = hub.defaultDataSource;
  const files = kb?.files ?? [];

  // Poll for status updates when files are processing
  useEffect(() => {
    const hasActiveFiles = files.some((f) => ["pending", "processing"].includes(f.status));
    if (!hasActiveFiles) return;

    const interval = setInterval(() => {
      onRefresh();
    }, 5000);

    return () => clearInterval(interval);
  }, [files, onRefresh]);

  const handleUpload = async (fileList: FileList | File[]) => {
    const filesToUpload = Array.from(fileList);
    if (filesToUpload.length === 0) return;

    try {
      setUploading(true);
      await projectService.uploadFiles(projectId, filesToUpload);
      showToast(`${filesToUpload.length} file${filesToUpload.length > 1 ? "s" : ""} uploaded`, "success");
    } catch (err) {
      console.error("Failed to upload files:", err);
      showToast("Failed to upload files", "error");
    } finally {
      setUploading(false);
      await onRefresh();
    }
  };

  const handleAddNote = async (title: string, content: string) => {
    if (!kb) return;

    try {
      setIsAddingNote(true);
      await dataSourceService.addNote(kb.uuid, { title, content });
      setShowNoteModal(false);
      showToast("Note added", "success");
    } catch (err) {
      console.error("Failed to add note:", err);
      showToast("Failed to add note", "error");
    } finally {
      setIsAddingNote(false);
      await onRefresh();
    }
  };

  const handleDeleteFile = async (file: ProjectKnowledgeBaseFile) => {
    const confirmed = await confirm({
      title: "Delete File",
      message: `Are you sure you want to delete "${file.filename}"? This will also remove it from the knowledge base index.`,
      confirmText: "Delete",
      variant: "danger",
    });
    if (!confirmed) return;

    try {
      await projectService.deleteFile(projectId, file.uuid);
      showToast("File deleted", "success");
      onRefresh();
    } catch (err) {
      console.error("Failed to delete file:", err);
      showToast("Failed to delete file", "error");
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    if (e.dataTransfer.files.length > 0) {
      handleUpload(e.dataTransfer.files);
    }
  };

  return (
    <section>
      <div className="flex items-center justify-between mb-4 gap-3">
        <div className="flex items-center gap-2 min-w-0">
          <BookOpen className="h-5 w-5 text-brand-primary shrink-0" />
          <h2 className="text-lg font-semibold text-content-primary whitespace-nowrap">Knowledge Base</h2>
          {kb && (
            <span className="text-xs text-content-quaternary bg-surface-secondary px-2 py-0.5 rounded-full whitespace-nowrap">
              {kb.fileCount} {kb.fileCount === 1 ? "file" : "files"}
            </span>
          )}
        </div>
        <div className="flex items-center gap-1 shrink-0">
          {canEdit && kb && (
            <button
              onClick={() => setShowNoteModal(true)}
              className="flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium text-content-secondary hover:text-content-primary hover:bg-interactive-hover rounded-lg transition-colors whitespace-nowrap"
              title="Add a text note"
            >
              <StickyNote className="h-3.5 w-3.5" />
              Add Note
            </button>
          )}
          {kb && (
            <button
              onClick={() => navigate(`/data-sources/${kb.uuid}`)}
              className="flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium text-content-secondary hover:text-content-primary hover:bg-interactive-hover rounded-lg transition-colors whitespace-nowrap"
              title="Open full data source view"
            >
              <ExternalLink className="h-3.5 w-3.5" />
              Manage
            </button>
          )}
        </div>
      </div>

      <div className="bg-surface-primary border border-border-primary rounded-xl overflow-hidden">
        {/* Upload zone */}
        {canEdit && (
          <div
            onDragOver={(e) => {
              e.preventDefault();
              setDragOver(true);
            }}
            onDragLeave={() => setDragOver(false)}
            onDrop={handleDrop}
            className={`p-4 border-b border-border-primary transition-colors ${
              dragOver ? "bg-brand-primary/5 border-brand-primary/30" : "bg-surface-primary"
            }`}
          >
            <input
              ref={fileInputRef}
              type="file"
              multiple
              accept={UPLOAD_ACCEPT_STRING}
              className="hidden"
              onChange={(e) => {
                if (e.target.files) {
                  handleUpload(e.target.files);
                  e.target.value = "";
                }
              }}
            />
            <button
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading}
              className="w-full flex flex-col items-center gap-2 py-4 border-2 border-dashed border-border-secondary rounded-lg hover:border-brand-primary/40 hover:bg-brand-primary/5 transition-all group"
            >
              {uploading ? (
                <Loader2 className="h-6 w-6 animate-spin text-brand-primary" />
              ) : (
                <Upload className="h-6 w-6 text-content-quaternary group-hover:text-brand-primary transition-colors" />
              )}
              <div className="text-center">
                <p className="text-sm font-medium text-content-secondary group-hover:text-brand-primary transition-colors">
                  {uploading ? "Uploading..." : "Upload files"}
                </p>
                <p className="text-xs text-content-quaternary mt-0.5">Drop files here or click to browse</p>
              </div>
            </button>
          </div>
        )}

        {/* File list */}
        {files.length > 0 ? (
          <div className="divide-y divide-border-primary">
            {files.map((file) => {
              const Icon = getFileIcon(file.mimeType);
              const fileStatus = getFileStatusConfig(file.status);
              return (
                <div
                  key={file.uuid}
                  className="flex items-center gap-3 px-4 py-3 hover:bg-interactive-hover transition-colors group"
                >
                  <div className="h-8 w-8 rounded-lg bg-surface-secondary flex items-center justify-center shrink-0">
                    <Icon className="h-4 w-4 text-content-tertiary" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-content-primary truncate">{file.filename}</p>
                    <div className="flex items-center gap-2 mt-0.5">
                      {file.fileSize && (
                        <span className="text-[11px] text-content-quaternary">{formatFileSize(file.fileSize)}</span>
                      )}
                      <span className={`text-[11px] ${fileStatus.color}`}>{fileStatus.label}</span>
                    </div>
                  </div>
                  {canEdit && (
                    <button
                      onClick={() => handleDeleteFile(file)}
                      className="p-1.5 text-content-quaternary hover:text-red-400 transition-colors opacity-0 group-hover:opacity-100"
                      title="Delete file"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        ) : (
          !canEdit && (
            <div className="p-6 text-center">
              <BookOpen className="h-8 w-8 text-content-quaternary mx-auto mb-2" />
              <p className="text-sm text-content-tertiary">No files in the knowledge base yet.</p>
            </div>
          )
        )}
      </div>

      <AddNoteModal
        open={showNoteModal}
        onClose={() => setShowNoteModal(false)}
        onSave={handleAddNote}
        isSaving={isAddingNote}
      />
    </section>
  );
}
